import { setCustomExpirationDate } from "@/components/auth/LazoLogin";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import LazoAuthService from "@/services/LazoAuthService";
import axios from "axios";
import moment from "moment";

/**
 * Recovers a session from the Domain=.lazo.one lazo_auth_token cookie when the current origin's
 * localStorage holds no token yet.
 *
 * This is the cold-boot path for a *.lazo.one workspace subdomain reached directly — e.g. the
 * auth-app forwards a signed-in user straight to <slug>.lazo.one/one/my-companies, never passing
 * through /redirect's OTC exchange. Without this, AuthService.isLogged() is false on first paint and
 * ProtectedRoute renders "Session Required" even though a valid .lazo.one cookie exists.
 *
 * Mirrors RedirectPage.establishSession: store the recovered token, prime the axios Authorization
 * default, then store the user so the route guards' permission checks
 * (AuthService.hasPermissionFor) pass — without storing the user, isLogged() is true but every
 * permission-gated route renders "Access Restricted". Cookie-gated server-side (GET
 * /api/lazo-auth/session returns 401 without a valid cookie), so this never logs in an anonymous
 * visitor.
 *
 * @returns true when a session was recovered and the user was hydrated; false on every failure path.
 */
export async function recoverSessionFromCookie(): Promise<boolean> {
  try {
    const response = await LazoAuthService.getSession();
    if (!response.isSuccess()) return false;

    const session = response.getContentUnchecked();
    if (!session?.token) return false;

    if (session.expiresAt) {
      AuthService.storeToken(session.token, moment(session.expiresAt).toDate());
    } else {
      AuthService.storeToken(session.token);
      setCustomExpirationDate();
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${session.token}`;

    // Hydrate the in-app User (carries the permissions ProtectedRoute checks). Without this the
    // token is stored but AuthService.getUser() is empty, so hasPermissionFor() is false and every
    // permission-gated route shows "Access Restricted" even for an authorized user. Fail closed:
    // if the user can't be resolved, drop the half-recovered session rather than land logged-in
    // without permissions.
    const userResponse = await LazoAuthService.getUserByToken(session.token);
    if (!userResponse.isSuccess()) {
      AuthService.logOut();
      return false;
    }
    const user = userResponse.getContentUnchecked();
    if (!user) {
      AuthService.logOut();
      return false;
    }
    AuthService.storeUser(user);
    return true;
  } catch {
    return false;
  }
}
